import { useDispatch } from "react-redux";
import { useCallback } from "react";
import {
  useSignaturesSelector,
  useFetchedDocumentSelector,
} from "../../../store/documents/DocumentSelector";
import { documentsActions } from "../../../store/documents/DocumentReducer";
import { Signature } from "../../../store/documents/DocumentReducer";
import { usePDF } from "../../../modules/pdf";
import { useToast } from "../../../components/toast";
import DigitalSignatureGateway from "../../../api/gateways/DigitalSignatureGateway";
import { useTranslation } from "react-i18next";
import { BlobCache } from "../utils/BlobCache";
import { extractQueryParamValue } from "../utils/QueryParams";

export const useHandleUserSignatures = () => {
  const dispatch = useDispatch();
  const signatures = useSignaturesSelector();
  const { applySignaturesToPdf } = usePDF();
  const fetchedDocumentData = useFetchedDocumentSelector();
  const { danger, success } = useToast();
  const { t } = useTranslation();

  const handleStartSigning = () => {
    dispatch(documentsActions.setIsSigning(true));
  };

  const addSignature = useCallback(
    (signature: Omit<Signature, "id">) => {
      const id = Date.now();
      dispatch(documentsActions.addSignature({ ...signature, id }));
    },
    [dispatch]
  );

  const removeSignature = useCallback(
    (id: number) => {
      const confirm = window.confirm("Are you sure you want to delete this signature?");
      if (confirm) {
        dispatch(documentsActions.removeSignature(id));
      }
    },
    [dispatch]
  );

  const clearSignatures = useCallback(() => {
    dispatch(documentsActions.clearSignatures());
  }, [dispatch]);

  const handleCancelSigning = () => {
    dispatch(documentsActions.setIsSigning(false));
    clearSignatures();
  };

  const handleSubmitSigning = async () => {
    if (signatures.length === 0) {
      danger(t("ERRORS.INVALID_SIGNED_DOCUMENT"));

      return;
    }

    const blob = BlobCache.get();
    const fileData = fetchedDocumentData?.files?.[0];

    if (!fileData || !blob) {
      danger(t("ERRORS.NO_FILE"));
      return;
    }

    try {
      const file = new File([blob], fileData?.name, {
        type: "application/pdf",
      });

      // Apply signatures and get the signed PDF blob
      const signedFile = await applySignaturesToPdf(file, signatures);

      if (!signedFile) {
        danger(t("ERRORS.EMBED_SIGNATURES"));
        return;
      }

      const token = extractQueryParamValue("token");
      const email = extractQueryParamValue("email");

      if (!token || !email) {
        danger(t("ERRORS.MISSING_TOKEN_OR_EMAIL"));
        return;
      }

      // Upload the signed PDF to the backend
      const formData = new FormData();
      formData.append("file", signedFile);
      formData.append("email", email);
      formData.append("token", token);

      // Send to backend
      const result = await DigitalSignatureGateway.submitWebSignedDocument(formData);

      if ("status" in result && "id" in result) {
        success(t("WEB_DIGITAL_SIGNATURE.SUBMITTED_MESSAGE"));

        dispatch(documentsActions.setIsSigning(false));
        dispatch(documentsActions.setDocumentSuccessfullySignedStatus(true));
        clearSignatures();
      } else {
        danger(t("ERRORS.SIGNATURE_SUBMISSION_FAILED"));
      }
    } catch (error) {
      danger(t("ERRORS.UNKNOWN"));
    }
  };

  return {
    signatures,
    addSignature,
    removeSignature,
    clearSignatures,
    handleCancelSigning,
    handleSubmitSigning,
    handleStartSigning,
  };
};
